/**
 * Targeting Rules Editor — edit flag.targeting_rules (priority-ordered).
 * Each rule: AND-ed conditions (attribute / operator / value) + result + priority.
 */

import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { useI18n } from '@/i18n';
import type { FlagResponse, TargetingRule, TargetingCondition } from '@/types/featureFlags';
import { useUpdateFlag } from '../hooks/useFeatureFlags';

interface TargetingRulesEditorProps {
  flag: FlagResponse;
}

const ATTRIBUTES = ['is_beta_tester', 'platform', 'system_role', 'user_groups'] as const;
const OPERATORS = ['eq', 'neq', 'in', 'not_in'] as const;

// Operators legal per attribute: a scalar bool only compares eq/neq; user_groups (a list
// attribute) only intersects via in/not_in; platform/system_role allow both. This blocks
// nonsensical combos (e.g. is_beta_tester in [...]) that the backend would silently no-match.
const OPERATORS_BY_ATTR: Record<string, ReadonlyArray<(typeof OPERATORS)[number]>> = {
  is_beta_tester: ['eq', 'neq'],
  user_groups: ['in', 'not_in'],
  platform: ['eq', 'neq', 'in', 'not_in'],
  system_role: ['eq', 'neq', 'in', 'not_in'],
};
const allowedOps = (attr: string): ReadonlyArray<(typeof OPERATORS)[number]> =>
  OPERATORS_BY_ATTR[attr] ?? OPERATORS;

// Closed-enum attributes whose values come from a fixed backend vocabulary
// (platform filter / SystemRole). Surfacing them as pickers — instead of a blank
// text box — is the point of the value-guidance work: the user no longer has to
// guess "ios" vs "iOS", and a typo can't silently produce a no-match rule.
const VALUES_BY_ATTR: Record<string, readonly string[]> = {
  platform: ['ios', 'android', 'web'],
  system_role: ['end_user', 'beta_tester', 'developer', 'admin'],
};
const hasEnumValues = (attr: string) => attr in VALUES_BY_ATTR;
const isMultiOp = (op: string) => op === 'in' || op === 'not_in';

type Attr = (typeof ATTRIBUTES)[number];

const isListAttr = (attr: string) => attr === 'user_groups';
const isBoolAttr = (attr: string) => attr === 'is_beta_tester';

/** Convert a stored condition value to an editor string. */
function valueToInput(value: TargetingCondition['value']): string {
  if (Array.isArray(value)) return (value as unknown[]).join(',');
  return String(value ?? '');
}

/** Convert editor string back to a typed value based on attribute/operator. */
function inputToValue(
  raw: string,
  attr: string,
  operator: string
): TargetingCondition['value'] {
  // Operator wins over attribute: in/not_in REQUIRE an array value — the backend evaluator
  // does `isinstance(expected, list)` and a string/bool would never match. Store as string[].
  if (operator === 'in' || operator === 'not_in' || isListAttr(attr)) {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  }
  if (isBoolAttr(attr)) return raw === 'true';
  return raw;
}

/**
 * Re-coerce a condition's value when its attribute or operator changes shape.
 * Membership ops / list attrs want string[]; is_beta_tester wants bool; eq/neq
 * want a single scalar. Crucially, switching multi→single must NOT comma-join the
 * old array into "ios,android" — that string matches no enum option, the select
 * renders blank, and saving leaves a silent no-match rule. We take the first
 * element instead, and drop it entirely if it isn't valid for a closed-enum attr.
 */
function coerceValue(
  prev: TargetingCondition['value'],
  attr: string,
  operator: string
): TargetingCondition['value'] {
  if (isMultiOp(operator) || isListAttr(attr)) {
    return inputToValue(valueToInput(prev), attr, operator);
  }
  if (isBoolAttr(attr)) return prev === true || prev === 'true';
  const first = Array.isArray(prev) ? (prev.length ? String(prev[0]) : '') : String(prev ?? '');
  if (hasEnumValues(attr) && !(VALUES_BY_ATTR[attr] as readonly string[]).includes(first)) {
    return '';
  }
  return first;
}

export function TargetingRulesEditor({ flag }: TargetingRulesEditorProps) {
  const { t } = useI18n();
  const updateMutation = useUpdateFlag();
  const [rules, setRules] = useState<TargetingRule[]>([]);

  useEffect(() => {
    // Deep clone so local edits don't mutate query cache.
    setRules(JSON.parse(JSON.stringify(flag.targeting_rules ?? [])));
  }, [flag.flag_key, flag.targeting_rules]);

  const sortedRules = [...rules].sort((a, b) => a.priority - b.priority);

  const addRule = () => {
    const nextPriority = rules.length === 0 ? 1 : Math.max(...rules.map((r) => r.priority)) + 1;
    setRules([
      ...rules,
      { priority: nextPriority, result: true, conditions: [{ attribute: 'platform', operator: 'eq', value: '' }] },
    ]);
  };

  const removeRule = (index: number) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  const updateRule = (index: number, patch: Partial<TargetingRule>) => {
    setRules(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const addCondition = (ruleIndex: number) => {
    setRules(
      rules.map((r, i) =>
        i === ruleIndex
          ? { ...r, conditions: [...r.conditions, { attribute: 'platform', operator: 'eq', value: '' }] }
          : r
      )
    );
  };

  const removeCondition = (ruleIndex: number, condIndex: number) => {
    setRules(
      rules.map((r, i) =>
        i === ruleIndex ? { ...r, conditions: r.conditions.filter((_, c) => c !== condIndex) } : r
      )
    );
  };

  const updateCondition = (ruleIndex: number, condIndex: number, patch: Partial<TargetingCondition>) => {
    setRules(
      rules.map((r, i) =>
        i === ruleIndex
          ? {
              ...r,
              conditions: r.conditions.map((c, ci) => {
                if (ci !== condIndex) return c;
                const merged = { ...c, ...patch };
                // Changing attribute may make the current operator illegal — clamp it.
                if ('attribute' in patch) {
                  const ops = allowedOps(merged.attribute);
                  if (!ops.includes(merged.operator as (typeof OPERATORS)[number])) {
                    merged.operator = ops[0];
                  }
                }
                // Re-coerce the existing value to the shape the new attr/operator needs
                // (scalar vs bool vs string[]). coerceValue avoids the multi→single
                // comma-join trap that would otherwise persist a silent no-match rule.
                if ('attribute' in patch || 'operator' in patch) {
                  merged.value = coerceValue(c.value, merged.attribute, merged.operator);
                }
                return merged;
              }),
            }
          : r
      )
    );
  };

  const handleSave = async () => {
    try {
      await updateMutation.mutateAsync({ flagKey: flag.flag_key, data: { targeting_rules: rules } });
      toast.success(t('featureFlags.rules.saved'));
    } catch {
      // toast already shown by mutation onError
    }
  };

  const attrLabel = (attr: Attr) =>
    ({
      is_beta_tester: t('featureFlags.rules.attrIsBetaTester'),
      platform: t('featureFlags.rules.attrPlatform'),
      system_role: t('featureFlags.rules.attrSystemRole'),
      user_groups: t('featureFlags.rules.attrUserGroups'),
    })[attr];

  const opLabel = (op: (typeof OPERATORS)[number]) =>
    ({
      eq: t('featureFlags.rules.opEq'),
      neq: t('featureFlags.rules.opNeq'),
      in: t('featureFlags.rules.opIn'),
      not_in: t('featureFlags.rules.opNotIn'),
    })[op];

  // Per-attribute hint telling the user what a valid value looks like.
  const valueGuidance = (attr: string): string =>
    (
      {
        is_beta_tester: t('featureFlags.rules.guidanceIsBetaTester'),
        platform: t('featureFlags.rules.guidancePlatform'),
        system_role: t('featureFlags.rules.guidanceSystemRole'),
        user_groups: t('featureFlags.rules.guidanceUserGroups'),
      } as Record<string, string>
    )[attr] ?? '';

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{t('featureFlags.rules.title')}</h3>
          <p className="text-sm text-gray-500">{t('featureFlags.rules.description')}</p>
        </div>
        <Button variant="secondary" onClick={addRule}>
          <Plus className="w-4 h-4 mr-2" />
          {t('featureFlags.rules.addRule')}
        </Button>
      </div>

      {sortedRules.length === 0 ? (
        <div className="text-center py-8 text-gray-500 border border-dashed border-gray-200 rounded-lg">
          <p className="text-sm">{t('featureFlags.rules.noRules')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sortedRules.map((rule) => {
            const ruleIndex = rules.indexOf(rule);
            return (
              <div key={ruleIndex} className="border border-gray-200 rounded-lg p-4 space-y-4">
                {/* Rule header */}
                <div className="flex items-center gap-4 flex-wrap">
                  <span className="text-sm font-medium text-gray-700">
                    {t('featureFlags.rules.rule')} #{ruleIndex + 1}
                  </span>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500">{t('featureFlags.rules.priority')}</label>
                    <input
                      type="number"
                      value={rule.priority}
                      onChange={(e) => updateRule(ruleIndex, { priority: Number(e.target.value) })}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500">{t('featureFlags.rules.result')}</label>
                    <Switch
                      size="sm"
                      checked={rule.result === true}
                      onChange={(v) => updateRule(ruleIndex, { result: v })}
                      ariaLabel={t('featureFlags.rules.result')}
                      offLabel={t('featureFlags.rules.resultOff')}
                      onLabel={t('featureFlags.rules.resultOn')}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeRule(ruleIndex)}
                    title={t('featureFlags.rules.removeRule')}
                    className="ml-auto p-1.5 text-gray-400 hover:text-red-600 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>

                {/* Conditions */}
                <div className="space-y-2">
                  <p className="text-xs font-medium text-gray-500">{t('featureFlags.rules.conditions')}</p>
                  {rule.conditions.map((cond, condIndex) => {
                    const valuePlaceholder = isListAttr(cond.attribute) || isMultiOp(cond.operator)
                      ? t('featureFlags.rules.valueListPlaceholder')
                      : t('featureFlags.rules.valuePlaceholder');
                    const enumValues = VALUES_BY_ATTR[cond.attribute] ?? [];
                    const selectedEnum = Array.isArray(cond.value) ? cond.value.map(String) : [];
                    const guidance = valueGuidance(cond.attribute);
                    return (
                      <div key={condIndex} className="space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {condIndex > 0 && (
                            <span className="text-xs font-medium text-gray-400 w-8">
                              {t('featureFlags.rules.and')}
                            </span>
                          )}
                          <Select
                            value={cond.attribute}
                            onChange={(e) =>
                              updateCondition(ruleIndex, condIndex, { attribute: e.target.value })
                            }
                            options={ATTRIBUTES.map((a) => ({ value: a, label: attrLabel(a) }))}
                            className="!w-40"
                          />
                          <Select
                            value={cond.operator}
                            onChange={(e) =>
                              updateCondition(ruleIndex, condIndex, { operator: e.target.value })
                            }
                            options={allowedOps(cond.attribute).map((o) => ({ value: o, label: opLabel(o) }))}
                            className="!w-32"
                          />
                          {isBoolAttr(cond.attribute) ? (
                            <Select
                              value={cond.value === true || cond.value === 'true' ? 'true' : 'false'}
                              onChange={(e) =>
                                updateCondition(ruleIndex, condIndex, { value: e.target.value === 'true' })
                              }
                              options={[
                                { value: 'true', label: 'true' },
                                { value: 'false', label: 'false' },
                              ]}
                              className="!w-32"
                            />
                          ) : hasEnumValues(cond.attribute) && isMultiOp(cond.operator) ? (
                            // Closed enum + membership operator → pick any subset via chips.
                            <div className="flex flex-wrap gap-1.5 flex-1 min-w-[8rem]">
                              {enumValues.map((v) => {
                                const selected = selectedEnum.includes(v);
                                return (
                                  <button
                                    key={v}
                                    type="button"
                                    onClick={() =>
                                      updateCondition(ruleIndex, condIndex, {
                                        value: selected
                                          ? selectedEnum.filter((x) => x !== v)
                                          : [...selectedEnum, v],
                                      })
                                    }
                                    className={clsx(
                                      'px-2.5 py-1 text-xs rounded-full border transition-colors',
                                      selected
                                        ? 'bg-primary-50 border-primary-300 text-primary-700'
                                        : 'bg-white border-gray-300 text-gray-600 hover:border-gray-400'
                                    )}
                                  >
                                    {v}
                                  </button>
                                );
                              })}
                            </div>
                          ) : hasEnumValues(cond.attribute) ? (
                            // Closed enum + eq/neq → single-pick dropdown.
                            <Select
                              value={typeof cond.value === 'string' ? cond.value : ''}
                              onChange={(e) =>
                                updateCondition(ruleIndex, condIndex, { value: e.target.value })
                              }
                              options={[
                                { value: '', label: t('featureFlags.rules.valueSelectPlaceholder') },
                                ...enumValues.map((v) => ({ value: v, label: v })),
                              ]}
                              className="!w-44"
                            />
                          ) : (
                            <div className="flex-1 min-w-[8rem]">
                              <Input
                                value={valueToInput(cond.value)}
                                onChange={(e) =>
                                  updateCondition(ruleIndex, condIndex, {
                                    value: inputToValue(e.target.value, cond.attribute, cond.operator),
                                  })
                                }
                                placeholder={valuePlaceholder}
                              />
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={() => removeCondition(ruleIndex, condIndex)}
                            title={t('featureFlags.rules.removeCondition')}
                            className="p-1.5 text-gray-400 hover:text-red-600 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        {guidance && (
                          <p className="text-xs text-gray-400 pl-0">{guidance}</p>
                        )}
                      </div>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => addCondition(ruleIndex)}
                    className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    {t('featureFlags.rules.addCondition')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-end pt-2 border-t border-gray-200">
        <Button variant="primary" onClick={handleSave} loading={updateMutation.isPending}>
          {t('featureFlags.rules.save')}
        </Button>
      </div>
    </div>
  );
}
